import React, { ReactElement, useRef, useEffect, PropsWithChildren } from 'react';
import { Tabs as MUITabs, Tab as MUITab } from '@mui/material';
import css from './Tabs.module.scss';
import { KeyboardDispatchTypes, useKeyboardDispatch } from '../../context/Keyboard/keyboard';
import { TabFocusRefContext } from './Tabs';

interface ITabs {
    activeTabIndex: number;
    onTabChange: (index: number) => void;
    children: (false | undefined | ReactElement<ITabProps>)[];
    noBottomMargin?: boolean;
}

export const Tabs = React.memo((props: ITabs) => {
    const keyboardDispatch = useKeyboardDispatch();

    useEffect(() => {
        const foundTab = props.children.find((val) => (val as ReactElement<ITabProps>)?.props?.tabIndex === props.activeTabIndex) as ReactElement<ITabProps>;
        if (foundTab) {
            const hideKeyboard = !!foundTab.props.hideKeyboard;
            keyboardDispatch({ type: KeyboardDispatchTypes.KeyboardHidden,
                payload: hideKeyboard });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.activeTabIndex]);

    const handleChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
        props.onTabChange(newValue);
    };

    return (
        <>
            <MUITabs
                classes={{ root: `${css.tabsBorder} ${css.tabs} ${props.noBottomMargin && css.noBottomMargin}`,
                    indicator: css.indicator }}
                value={props.activeTabIndex}
                onChange={handleChange}
                indicatorColor={'primary'}
                textColor={'primary'}
                variant={'fullWidth'}
            >
                {React.Children.toArray(props.children)
                    .map((child, index) => (
                        <MUITab
                            classes={{ root: css.tab,
                                selected: css.tabSelected }}
                            key={index}
                            title={(child as ReactElement<ITabProps>)?.props.title}
                            label={<h2 className={css.noHeaderStyle}>{(child as ReactElement<ITabProps>)?.props.label}</h2>}
                        />
                    ))}
            </MUITabs>
            {React.Children.toArray(props.children)}
        </>
    );
});

interface ITabProps {
    active: boolean;
    tabIndex: number;
    label: string;
    title?: string;
    hideKeyboard?: boolean;
    addContainer?: boolean;
}

export const Tab: React.FC<PropsWithChildren<ITabProps>> = React.memo((props) => {
    const ref: React.MutableRefObject<any> = useRef();

    React.useEffect(() => {
        if (props.active) {
            ref.current?.focus();
        }
    }, [props.active]);

    return (
        <TabFocusRefContext.Provider value={ref}>
            <div
                className={props.addContainer ? css.tabContainer : ''}
                role={'tabpanel'}
                hidden={!props.active}
            >
                {props.children}
            </div>
        </TabFocusRefContext.Provider>
    );
});
